import { useState } from "react";
import { useSearchParams, Link } from "react-router";
import { useApproveDeviceCode } from "@/hooks/api/useDeviceCodes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Device pairing approval page (/activate?code=XXXXXX). The plugin opens
 * this URL with the code pre-filled; the user just clicks Approve. Reached
 * logged-out, ProtectedRoute detours through login/signup and returns here.
 */
export default function Activate() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get("code") ?? "");
  const approve = useApproveDeviceCode();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    approve.mutate(code.trim());
  };

  if (approve.isSuccess) {
    const deviceName = approve.data.data.deviceName;
    return (
      <div className="max-w-xl">
        <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-8 text-center">
          <h1 className="text-xl font-medium uppercase tracking-wider mb-2">
            Plugin Connected
          </h1>
          <p className="text-muted-foreground mb-6">
            {deviceName ? `"${deviceName}" is` : "Your plugin is"} now linked
            to your account. You can return to the plugin — it will finish
            connecting on its own.
          </p>
          <Link to="/jams">
            <Button variant="outline">Go to My Jams</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl">
      <form
        onSubmit={handleSubmit}
        className="bg-[#191919] border border-[#616161]/20 rounded-lg p-8 text-center"
      >
        <h1 className="text-xl font-medium uppercase tracking-wider mb-2">
          Connect Your Plugin
        </h1>
        <p className="text-muted-foreground mb-4">
          Approve this code to let the Cobar plugin access your account.
        </p>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-left text-sm text-amber-200/90 mb-6">
          <p className="font-medium mb-1">Before you approve</p>
          <p>
            Only approve a code that is showing in your own Cobar plugin right
            now (Settings &rarr; Connect Account). Approving grants that
            plugin full access to your account — never approve a code someone
            sent you.
          </p>
        </div>
        <div className="max-w-xs mx-auto text-left space-y-2 mb-6">
          <Label htmlFor="deviceCode">Pairing code</Label>
          <Input
            id="deviceCode"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. H7KQ4F"
            required
            maxLength={12}
            className="font-mono uppercase text-center text-lg tracking-[0.3em]"
          />
        </div>
        <Button type="submit" disabled={approve.isPending || !code.trim()}>
          {approve.isPending ? "Approving..." : "Approve"}
        </Button>
        {approve.isError && (
          <p className="text-sm text-destructive mt-4">
            {approve.error?.message === "Invalid or expired code"
              ? "That code is invalid or has expired. Start the connection again from the plugin."
              : approve.error?.message || "Failed to approve device"}
          </p>
        )}
      </form>
    </div>
  );
}
